/**
 * SortableItem -- A single drag-and-drop sortable item using @dnd-kit.
 *
 * Wraps children with a drag handle and sortable behaviour.
 * Used inside SortableList to make any content reorderable.
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';

export interface SortableItemProps {
  id: string;
  children: ReactNode;
}

export function SortableItem({ id, children }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-item ${isDragging ? 'sortable-item--dragging' : ''}`}
    >
      <button
        type="button"
        className="sortable-item__handle"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <span aria-hidden="true">&#x2630;</span>
      </button>
      <div className="sortable-item__content">{children}</div>
    </div>
  );
}
